import { AdditionalServicesService } from '@additional-services/services/additional-services.service';
import { GACompleteOrderFormErrors } from '@analytics/classes/ga.complete.class';
import { GACompleteOrderNoDimensionError } from '@analytics/classes/ga.offers.class';
import { AnalyticsService } from '@analytics/services/analytics.service';
import { GaEventService } from '@analytics/services/ga.event.service';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  FormGroupDirective,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { CompleteOrderService } from '@complete-order/services/complete-order.service';
import { BaseComponent } from '@core/components/base/base.component';
import {
  CurrentPlan,
  DeliveryControls,
  DeliveryDatesRequest,
  DeliveryErrors,
  DirectDebitControls,
  DirectDebitErrors,
  DirectDebitFormState,
  ErrorType,
  fadeInOut,
  FormStep,
  Offers,
  ReplacementOffer,
  Service,
  slide,
  slideInOut,
  UpfrontPaymentSummary,
} from '@dgx-pr-spa/shared/util';
import { BuildConfigService } from '@dgx-pr-spa/ui-components';
import { Store } from '@ngrx/store';
import { LiveChatService } from '@pages/services/live-chat.service';
import { ProductDetailsService } from '@product-details/services/product-details.service';
import { selectDeliveryDate } from '@product-details/store/actions/selected.delivery.date.action';
import { selectSelectedDate } from '@product-details/store/selectors/selected.delivery.date.selector';
import { ViewOffersService } from '@view-offers/services/view-offers.service';
import { BehaviorSubject, combineLatest, Observable, throwError } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import {
  accessDifficultiesFieldDef,
  additionalInfoFieldDef,
  checkSpecFieldDef,
  emailFieldDef,
  mobileFieldDef,
  mobileValidation,
  productionConnectionFieldDef,
  stairsFieldDef,
  telephoneFieldDef,
} from './order-form-config';

@Component({
  selector: 'app-order-form',
  templateUrl: './order-form.component.html',
  styleUrls: ['./order-form.component.scss'],
  animations: [fadeInOut, slideInOut, slide],
})
export class OrderFormComponent
  extends BaseComponent
  implements AfterViewInit, OnChanges, OnInit
{
  constructor(
    public readonly completeOrderService: CompleteOrderService,
    public readonly productDetailsService: ProductDetailsService,
    public readonly additionalServicesService: AdditionalServicesService,
    public readonly viewOffersService: ViewOffersService,
    public readonly buildConfigService: BuildConfigService,
    public analyticsService: AnalyticsService,
    public gaEventService: GaEventService,
    private readonly cdr: ChangeDetectorRef,
    private router: Router,
    private store: Store,
    private readonly liveChatService: LiveChatService
  ) {
    super();
  }

  emailFieldDef = emailFieldDef;
  mobileFieldDef = mobileFieldDef;
  telephoneFieldDef = telephoneFieldDef;
  accessDifficultiesFieldDef = accessDifficultiesFieldDef;
  additionalInfoFieldDef = additionalInfoFieldDef;
  stairsFieldDef = stairsFieldDef;
  checkSpecFieldDef = checkSpecFieldDef;
  productionConnectionFieldDef = productionConnectionFieldDef;

  validateFormGroup = false;
  validateEmail = false;
  validateMobile = false;
  validateTelephone = false;
  /**
   * Enums
   */
  DeliveryControls = DeliveryControls;
  DirectDebitControls = DirectDebitControls;
  DeliveryErrors = DeliveryErrors;
  DirectDebitErrors = DirectDebitErrors;
  DirectDebitFormState = DirectDebitFormState;
  FormStep = FormStep;
  ErrorType = ErrorType;

  @ViewChild('FormWrapper', { static: false })
  formWrapper: FormGroupDirective;

  /**
   * Receives form initialised with basic form data
   * The store data (if any) is then patched by the
   * `connectForm` directive in the template
   */
  @Input() form: UntypedFormGroup;

  /**
   * Receives the active step state
   */
  @Input() activeStep: FormStep;

  /**
   * Receives the current plan
   */
  @Input() currentPlan = {} as CurrentPlan;

  /**
   * Receives boolean if upsell protection is selected
   */
  @Input() isProtectionSelected;

  /**
   * Receives selected offer
   */
  @Input() selected: ReplacementOffer;

  /**
   * Receives upfrontPaymentSummary
   */
  @Input() upfrontPaymentSummary: UpfrontPaymentSummary;

  /**
   * Receives upfrontPaymentSummary
   */
  @Input() offers: Offers;

  replacementCost$ = this.completeOrderService.replacementCost$;
  directDebitReuse$ = this.completeOrderService.directDebitReuse$;
  directDebitAutofillAvailable$ =
    this.completeOrderService.directDebitAutofillAvailable$;

  /**
   * Observable of form data from the store
   */
  form$ = this.completeOrderService.formData$;

  /**
   * Form stepper steps by name
   */
  steps = [];
  textAreaLimit = 200;
  disabledDates: Date[];
  minimumDate;
  maximumDate;
  telFieldisOpen = false;
  productMeasurements: string;
  setToFirstDate = false;

  /**
   * Observable of the optimizely state
   */
  optimizely$ = this.viewOffersService.optimizely$;

  selectedDate$: Observable<string | null>;
  dateFound$ = new BehaviorSubject<boolean>(null);
  isLoading$ = new BehaviorSubject<boolean>(false);
  deliveryDates$ = new BehaviorSubject<string[]>(null);
  supplierToArrange = false;
  disableContinue = false;
  calendarExpanded = false;

  ngOnInit() {
    this.selectedDate$ = this.store.select(selectSelectedDate);

    this.getDeliveryDates();
    this.watchDeliveryDateFields();
    this.watchTelephoneField();

    this.completeOrderService.resetCreateOrder();
    this.completeOrderService.updateActiveFormStep(FormStep.Delivery);
    this.productMeasurements =
      this.selected?.key_features?.find((k) => k.name === 'Measurements')
        ?.value || '';
  }

  onCalendarShow(): void {
    this.calendarExpanded = true;
    this.updateCalendarAriaAttributes();
  }

  onCalendarHide(): void {
    this.calendarExpanded = false;
    this.updateCalendarAriaAttributes();
  }

  updateCalendarAriaAttributes(): void {
    try {
      const input = document.getElementById('eDate') as HTMLInputElement;
      if (input) {
        input.setAttribute('aria-expanded', String(this.calendarExpanded));

        if (this.calendarExpanded) {
          const panel = document.querySelector(
            '[id$="_panel"], .p-datepicker, .p-calendar-panel, .p-datepicker-panel'
          ) as HTMLElement;
          if (panel) {
            if (!panel.id) {
              panel.id = `datepicker-panel-${Math.random()
                .toString(36)
                .slice(2, 9)}`;
            }
            input.setAttribute('aria-controls', panel.id);
          }
        } else {
          const controls = input.getAttribute('aria-controls');
          if (controls && !document.getElementById(controls)) {
            input.removeAttribute('aria-controls');
          }
        }

        if (input.hasAttribute('aria-haspopup')) {
          input.removeAttribute('aria-haspopup');
        }
      }

      const trigger = document.querySelector(
        'button.p-datepicker-trigger[aria-label="Choose Date"]'
      ) as HTMLButtonElement;
      if (trigger) {
        if (this.calendarExpanded) {
          const panelBtn = document.querySelector(
            '[id$="_panel"], .p-datepicker, .p-calendar-panel, .p-datepicker-panel'
          ) as HTMLElement;
          if (panelBtn) {
            if (!panelBtn.id) {
              panelBtn.id = `datepicker-panel-${Math.random()
                .toString(36)
                .slice(2, 9)}`;
            }
            trigger.setAttribute('aria-controls', panelBtn.id);
          }
        } else {
          const controlsBtn = trigger.getAttribute('aria-controls');
          if (controlsBtn && !document.getElementById(controlsBtn)) {
            trigger.removeAttribute('aria-controls');
          }
        }
        if (trigger.hasAttribute('aria-haspopup')) {
          trigger.removeAttribute('aria-haspopup');
        }
      }
    } catch (e) {
      console.error('Failed updating calendar ARIA attributes', e);
    }
  }

  validateField(field) {
    if (this.delCtrl(field).value) {
      this.delCtrl(field).markAsTouched();
      return true;
    }
    return false;
  }

  /**
   * Detect changes for form errors on load
   */
  ngAfterViewInit() {
    this.gaEventService.CompleteOrderLoadDeliveryStep();
    this.cdr.detectChanges();
    this.form.updateValueAndValidity();
    this.updateCalendarAriaAttributes();

    const prePopFields: {
      field: DeliveryControls;
      flag: 'validateEmail' | 'validateMobile' | 'validateTelephone';
    }[] = [
      { field: DeliveryControls.Email, flag: 'validateEmail' },
      { field: DeliveryControls.Mobile, flag: 'validateMobile' },
      { field: DeliveryControls.Telephone, flag: 'validateTelephone' },
    ];
    prePopFields.forEach(({ field, flag }) => {
      const ctrl = this.delCtrl(field);
      if (!ctrl) return;
      ctrl.valueChanges
        .pipe(
          startWith(ctrl.value),
          filter((val) => !!val),
          take(1),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          ctrl.markAsTouched();
          this[flag] = true;
          this.cdr.detectChanges();
        });
    });

    // if no product measurements are availble then disabled input and trigger GA
    if (!this.productMeasurements) {
      this.delCtrl(DeliveryControls.CheckSpecFieldDef).clearValidators();
      this.delCtrl(DeliveryControls.CheckSpecFieldDef).updateValueAndValidity();
      this.analyticsService.eventEmitter(
        new GACompleteOrderNoDimensionError(this.offers)
      );
    }
  }

  /**
   * Get the delivery dates
   */
  getDeliveryDates(): void {
    this.handleCalendarDisabledState();
    this.isLoading$.next(true);
    this.deliveryDates$.next(null);
    this.disableContinue = false;

    combineLatest([
      this.additionalServicesService.extras$.pipe(take(1)),
      this.productDetailsService.getDeliveryDatesRequestArgs$.pipe(take(1)),
    ]).subscribe(([extras, args]) => {
      const serviceCatNumbers = this.mapServiceCatNumbers(extras);

      const baseArgs: DeliveryDatesRequest = {
        offer_reference: args?.offer_reference as string,
        post_code: args?.post_code,
        supplier_id: args?.supplier_id ?? 0,
        cat_num: args?.cat_num,
        service_cat_numbers: null,
      };
      const payload: DeliveryDatesRequest = {
        ...baseArgs,
        service_cat_numbers: serviceCatNumbers.length
          ? serviceCatNumbers
          : null,
      };

      this.productDetailsService.loadDeliveryDates(payload);
    });

    this.productDetailsService.deliveryDatesState$
      .pipe(
        takeUntil(this.destroy$),
        filter(({ loaded, loading }) => loaded && !loading),
        take(1),
        map(({ data }) => data),
        catchError((error) => {
          this.isLoading$.next(false);
          console.error('Error loading delivery dates:', error);
          this.deliveryDates$.next(null);
          return throwError(() => new Error('Failed to load delivery dates'));
        })
      )
      .subscribe({
        next: (data) => {
          const deliveryDates = data.delivery_dates;

          const isSupplier =
            data.message?.toLowerCase() === 'supplier to arrange';
          this.supplierToArrange = deliveryDates === null && isSupplier;
          this.setDeliveryDates(deliveryDates);

          this.selectedDate$
            .pipe(
              take(1),
              tap((selectedDate) => {
                if (selectedDate) {
                  const dateFound = this.isMatchingDateExist(
                    deliveryDates,
                    selectedDate
                  );
                  this.dateFound$.next(dateFound);
                  if (dateFound) {
                    this.delCtrl(DeliveryControls.ExpectedDate).setValue(
                      selectedDate
                    );
                    //update the checkbox and calendar when first date selected through checkbox
                    if (selectedDate === deliveryDates[0]) {
                      this.setFirstAvailableDate();
                      this.delCtrl(
                        DeliveryControls.FirstAvailableDate
                      ).setValue(true);
                    }
                  }
                }
              })
            )
            .subscribe({
              complete: () => {
                setTimeout(() => this.isLoading$.next(false), 0);
              },
            });
        },
        error: (err) => {
          console.error('Subscription error:', err);
          this.isLoading$.next(false);
        },
      });
  }

  handleCalendarDisabledState(): void {
    this.isLoading$.subscribe((isLoading) => {
      const control = this.delCtrl(DeliveryControls.ExpectedDate);
      const hasDateRange = this.minimumDate && this.maximumDate;
      if (isLoading || !hasDateRange) {
        control?.disable();
      } else {
        control?.enable();
      }
    });
  }

  getErrorMessage$(): Observable<string | null> {
    return combineLatest([
      this.selectedDate$,
      this.dateFound$,
      this.deliveryDates$,
    ]).pipe(
      take(1),
      map(([selectedDate, dateFound, deliveryDates]) => {
        if (this.supplierToArrange) return null;

        // Remove text to select another product if it is a bespoke offer
        const noDeliveryDatesMessage =
          this.offers?.replacement_offers?.length > 1
            ? DeliveryErrors.NoDeliveryDates
            : `${DeliveryErrors.NoDeliveryDates.split('.')[0]}.`;

        const hasDates = deliveryDates?.length > 0;
        const isExpectedDateInvalid = this.isInvalid(
          DeliveryControls.ExpectedDate
        );

        if (isExpectedDateInvalid) {
          return hasDates
            ? DeliveryErrors.ExpectedDateRequired
            : noDeliveryDatesMessage;
        }

        if (dateFound === false && hasDates) {
          return this.replaceDate(
            DeliveryErrors.DeliveryDateTaken,
            selectedDate
          );
        }

        if (!hasDates) {
          return noDeliveryDatesMessage;
        }

        return null;
      })
    );
  }

  /**
   * Sets up the delivery dates
   * Setup min and max date range and disabledDates
   * Clear calendar validation on no returned dates
   * Appply conditional UI to the user
   */
  setDeliveryDates(delivery_dates: string[]): void {
    const dates = delivery_dates;
    this.deliveryDates$.next(dates || []);

    this.getDisabledDates(dates);

    if (dates?.length) {
      if (dates.length === 1) {
        this.setFirstAvailableDate();
      }
    } else {
      this.delCtrl(DeliveryControls.ExpectedDate).setValidators([]);
      this.delCtrl(DeliveryControls.ExpectedDate).updateValueAndValidity();
    }
  }

  /**
   * Setup range of dates from min - max retrieved from API
   * Set minDate and maxDate for calendar UI
   */
  getRangeDays(deliveryDates: string[]): string[] {
    if (deliveryDates?.length === 0) {
      this.minimumDate = undefined;
      this.maximumDate = undefined;
      return [];
    }
    this.minimumDate = new Date(deliveryDates[0]);
    this.maximumDate = new Date(deliveryDates[deliveryDates.length - 1]);

    const rangeArray = [];
    const date = new Date(deliveryDates[0]);

    while (date <= this.maximumDate) {
      rangeArray.push(new Date(date).toJSON().slice(0, 10));
      date.setDate(date.getDate() + 1);
    }

    return rangeArray;
  }

  private mapServiceCatNumbers(services: Service[] = []): string[] {
    if (!services?.length) {
      return [];
    }

    const codes = services
      .map(({ cat_num }) => (cat_num ? `${cat_num}` : null))
      .filter((code): code is string => !!code);

    return Array.from(new Set(codes));
  }

  /**
   * Setup disabled date for calendar
   */
  getDisabledDates(deliveryDates: string[]): void {
    if (!deliveryDates || deliveryDates.length === 0) {
      this.disabledDates = this.getRangeDays([]).map((item) => new Date(item));
      this.disableContinue = true;
      return;
    }

    const range = this.getRangeDays(deliveryDates);
    const deliverySet = new Set(deliveryDates.map((day) => day.slice(0, 10)));

    this.disabledDates = range
      .filter((item) => !deliverySet.has(item))
      .map((item) => new Date(item));
  }

  /**
   * Watch for changes on:-
   * FirstAvailableDate: if checked - set ExpectedDate to deliveryDates[0]
   * ExpectedDate: if date selected - set FirstAvailableDate to false
   */
  watchDeliveryDateFields() {
    this.delCtrl(DeliveryControls.FirstAvailableDate)
      .valueChanges.pipe(distinctUntilChanged())
      .subscribe({
        next: (val) => {
          if (!val) return;

          this.setFirstAvailableDate();
        },
      });

    this.delCtrl(DeliveryControls.ExpectedDate)
      .valueChanges.pipe(
        startWith(this.delCtrl(DeliveryControls.ExpectedDate)),
        distinctUntilChanged(),
        filter(() => {
          if (this.setToFirstDate) {
            this.setToFirstDate = false;
            return false;
          }

          return true;
        })
      )
      .subscribe({
        next: () => {
          this.delCtrl(DeliveryControls.FirstAvailableDate).setValue(false);
        },
      });
  }

  setTelephoneState(isTelephoneOpen: boolean) {
    this.delCtrl(DeliveryControls.IsTelephoneOpen).setValue(!isTelephoneOpen);

    if (isTelephoneOpen) {
      this.delCtrl(DeliveryControls.Telephone).setValidators([]);
    }
  }

  /**
   * Sets the first available date to expectedDate
   */
  setFirstAvailableDate() {
    const dates = this.deliveryDates$.value;
    if (dates?.length) {
      const date = dates[0].slice(0, 10);
      const expectedDate = date.split('-').reverse().join('/');
      this.setToFirstDate = true;
      this.delCtrl(DeliveryControls.ExpectedDate).setValue(expectedDate);
      this.delCtrl(DeliveryControls.ExpectedDate).markAsTouched();
    }
  }

  /**
   * Watch the telephone field for changes
   * Change mobile validations accordingly
   */
  watchTelephoneField() {
    this.delCtrl(DeliveryControls.Telephone)
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((val) => {
        this.setMobileValidators(val);
      });
  }

  /**
   * Conditionally sets mobile validators
   * Accepts the value of the telephone field
   */
  setMobileValidators(val: string) {
    if (!val) {
      this.delCtrl(DeliveryControls.Mobile).setValidators([
        mobileValidation,
        Validators.required,
      ]);
      this.delCtrl(DeliveryControls.Telephone).setValidators(mobileValidation);
    } else {
      this.delCtrl(DeliveryControls.Mobile).setValidators(mobileValidation);
      this.delCtrl(DeliveryControls.Telephone).setValidators([
        mobileValidation,
        Validators.required,
      ]);
      this.telFieldisOpen = true;
    }
  }

  /**
   * Rebuild steps array when new form is received
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes.form) {
      this.createSteps();
      this.listenToFormValueChanges();
    }
    if (changes.isProtectionSelected && !this.isProtectionSelected) {
      this.removeDirectDebitForm();
    }
  }

  listenToFormValueChanges(): void {
    this.form.valueChanges.pipe(debounceTime(10)).subscribe({
      next: (val) => {
        this.completeOrderService.updateOrderForm(val);
      },
    });
  }

  reValidateForm(): void {
    this.form.patchValue(this.form.value);
  }

  /**
   * Get the delivery control
   */
  delCtrl(name: DeliveryControls) {
    return (this.form.get(FormStep.Delivery) as UntypedFormGroup).get(name);
  }

  isValid(name) {
    return (
      this.delCtrl(name).valid &&
      (this.delCtrl(name).dirty || this.delCtrl(name).pristine)
    );
  }

  isInvalid(name) {
    return (
      this.delCtrl(name).invalid &&
      (this.delCtrl(name).touched ||
        (this.delCtrl(name).pristine && this.delCtrl(name).value))
    );
  }

  /**
   * Call GA form error
   */
  gaFormErrors(step: FormStep) {
    this.analyticsService.eventEmitter(
      new GACompleteOrderFormErrors(this.offers, step, this.form).gaData
    );
  }

  /**
   * Call GA form click
   */
  gaFormClick(step: FormStep) {
    if (step === FormStep.DirectDebit) {
      this.gaEventService.CompleteOrderLoadDirectDebitStep();
    } else if (step === FormStep.UpfrontPayment) {
      this.gaEventService.CompleteOrderLoadPaymentStep();
    }
  }

  /**
   * Dispatch store action when form edit is clicked.
   */
  onEditClick(step: FormStep) {
    if (step === FormStep.Delivery) {
      this.getDeliveryDates();
    }
    this.completeOrderService.updateActiveFormStep(step);
  }

  /**
   * Scrolls form into view
   */
  scrollIntoView(selector) {
    const firstElement = document.querySelector(selector) as HTMLElement;
    if (firstElement) {
      firstElement.scrollIntoView({ behavior: 'smooth' });
    }
  }

  /**
   * Build the steps as an array of strings
   */
  createSteps() {
    this.steps = [];
    Object.keys(this.form.controls).forEach((key) => {
      this.steps.push(key);
    });
  }

  /**
   * Determines whether edit is available
   */
  isEditAvailable(step: FormStep) {
    switch (step) {
      case FormStep.Delivery:
        return (
          this.form.get(FormStep.Delivery).valid && this.activeStep !== step
        );

      case FormStep.DirectDebit:
        return (
          this.form.get(FormStep.Delivery).valid &&
          this.form.get(FormStep.DirectDebit).valid &&
          this.activeStep !== step &&
          this.activeStep !== FormStep.Delivery
        );
      default:
        return false;
    }
  }

  /**
   * Update store with new step when continue clicked
   */
  onContinueClick(step: FormStep) {
    this.form.get(step).markAllAsTouched();

    this.validateFormGroup = true;

    this.reValidateForm();

    this.completeOrderService.resetCreateOrder();
    if (step === FormStep.DirectDebit) {
      this.completeOrderService.resetDirectDebitAttempts();
    }

    if (this.form.get(step).valid) {
      const nextStep = this.steps.indexOf(step) + 1;
      this.completeOrderService.updateActiveFormStep(this.steps[nextStep]);
      this.gaFormClick(this.steps[nextStep]);
      this.scrollIntoView('form');
    } else {
      this.gaFormErrors(step);
      setTimeout(() => {
        this.scrollIntoView('fieldset .ng-invalid');
      }); // push to end of stack
    }
  }

  /**
   * Remove the direct debit form
   */
  removeDirectDebitForm() {
    this.form.removeControl(FormStep.DirectDebit);
    this.form.updateValueAndValidity();
    this.createSteps();
  }

  /**
   * Dispatch store action to create an order with current store details
   */
  onCreateOrderClick() {
    this.completeOrderService.createOrder();
  }

  routePrompt(route: boolean) {
    if (route) {
      this.router.navigate(['/offers'], {
        queryParamsHandling: 'preserve',
      });
      return;
    }

    if (window['Genesys']) {
      this.liveChatService.openLiveChat();
    }
  }

  onDateSelect(selectedDate: string): void {
    this.dateFound$.next(null);
    this.store.dispatch(selectDeliveryDate({ selectedDate: selectedDate }));
  }

  onCheckboxClick(clicked: boolean): void {
    this.dateFound$.next(null);
    const dates = this.deliveryDates$.value;
    if (dates?.length && clicked) {
      this.store.dispatch(selectDeliveryDate({ selectedDate: dates[0] }));
    } else {
      this.store.dispatch(selectDeliveryDate({ selectedDate: '' }));
      this.delCtrl(DeliveryControls.ExpectedDate).setValue('');
    }
  }

  isMatchingDateExist(dateList: string[], date: string): boolean {
    const selectedDateFormatted = new Date(date).toISOString().split('T')[0];

    return dateList.some((dateStr) =>
      dateStr.startsWith(selectedDateFormatted)
    );
  }

  replaceDate(value: string, date: string): string {
    return value.replace('@date', this.formatDate(date));
  }

  formatDate(date: string): string {
    const dateItem = new Date(date);
    const day = dateItem.getDate();
    const month = dateItem.getMonth() + 1;
    const year = dateItem.getFullYear();

    return `${day}/${month < 10 ? `0${month}` : month}/${year}`;
  }
}
